import LeagueChoice from "./LeagueChoice";
import { LeagueDisplay } from "./LeagueDisplay";
import LeagueEvolutionChart from "./LeagueEvolutionChart";
import { evolutionAnalysisContract, evolutionSeries, evolutionSummary } from "../helpers/leagueEvolutionAnalysis";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { tipsRequest } from "../helpers/tipsWorkflow";
import {
  evolutionMatrix,
  evolutionValue,
  sortEvolutionRows,
} from "../helpers/leagueEvolution";
import "./LeagueEvolution.css";

export default function LeagueEvolution() {
  const { t: translate, i18n } = useTranslation();
  const t = (key, values) => translate(`help.leagueEvolution.${key}`, values);
  const [showTrend, setShowTrend] = useState(false), [showAnalysis, setShowAnalysis] = useState(false), [selectedLeague,setSelectedLeague] = useState("");
  const [records, setRecords] = useState([]),
    [leagues, setLeagues] = useState([]);
  const [busy, setBusy] = useState(true),
    [error, setError] = useState(false),
    [retry, setRetry] = useState(0);
  const [metric, setMetric] = useState("goals"),
    [mode, setMode] = useState("actual"),
    [sort, setSort] = useState({ key: "default", direction: "asc" });
  const readMetric = ["promoted", "relegated"].includes(metric) ? metric : "goals";
  const loaded = useRef(new Map());
  useEffect(() => {
    let active = true;
    setBusy(true);
    setError(false);
    setRecords([]);
    (async () => {
      let result = loaded.current.get(`${retry}:${readMetric}`);
      if (!result) {
        result = await tipsRequest("league-evolution", {mode:"matrix",metric:readMetric});
        if (!Array.isArray(result.records) || !Array.isArray(result.leagues)) throw Error("INVALID_RESPONSE");
        if (active) loaded.current.set(`${retry}:${readMetric}`,result);
      }
      if (!active) return;
      setRecords(result.records);
      setLeagues(result.leagues);
    })()
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [retry, readMetric]);
  const matrix = useMemo(
    () => evolutionMatrix(records, leagues),
    [records, leagues]
  );
  const strength = ["promoted", "relegated"].includes(metric);
  const relevantRows = useMemo(() => matrix.rows.filter(r => !strength || r.records.some(record => record.transitions?.[metric])), [matrix,strength,metric]);
  const rows = sort.key === "default" ? relevantRows : sortEvolutionRows(
    relevantRows,
    matrix.seasons,
    metric,
    mode,
    sort
  );
  const all = useMemo(() => relevantRows.flatMap((r) => r.records), [relevantRows]);
  const selectedRow = relevantRows.find(r => String(r.id) === selectedLeague) || relevantRows[0];
  const analysis = useMemo(() => showAnalysis && !busy && !error ? evolutionAnalysisContract(matrix, metric, mode) : null,
    [showAnalysis,busy,error,matrix,metric,mode]);
  const trend = useMemo(() => showTrend && selectedRow && !busy ? {
    summary:evolutionSummary(selectedRow.records,matrix.seasons,metric,mode),
    reference:evolutionSeries(all,matrix.seasons,metric,mode)
  } : null, [showTrend,selectedRow,busy,matrix,metric,mode,all]);
  const references = useMemo(() => Object.fromEntries([null,...matrix.seasons.map(s=>s.key)]
    .map(season=>[season,evolutionValue(all,metric,mode,season).value])), [matrix,metric,mode,all]);
  function format(value) {
    if (value == null) return "—";
    const n = metric === "draw" ? value * 100 : value;
    return (
      new Intl.NumberFormat(i18n?.language || "de", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay: mode === "difference" ? "exceptZero" : "auto",
      }).format(n) +
      (metric === "draw" ? (mode === "difference" ? ` ${t("pp")}` : " %") : "")
    );
  }
  function cell(list, season, reference) {
    const result = evolutionValue(list, metric, mode, season);
    const direction =
      result.value == null || reference == null || result.value === reference
        ? ""
        : result.value > reference
        ? "above"
        : "below";
    return (
      <span
        className={`le-value ${direction}`}
        title={strength ? t("teamSample", {count: result.teams}) : mode === "difference" && result.actualGames != null
          ? t("comparisonSample", { actual: result.actualGames, expected: result.games })
          : t("sample", { count: result.games })}
      >
        {format(result.value)}
        {mode === "difference" && result.value != null && (
          <small
            aria-label={t(
              result.value === 0 ? "equal" : `${metric}${result.value > 0 ? "Higher" : "Lower"}`
            )}
          >
            {result.value > 0 ? " ↑" : result.value < 0 ? " ↓" : " ="}
          </small>
        )}
      </span>
    );
  }
  function heading(key, label) {
    return (
      <th
        key={key}
        scope="col"
        aria-sort={
          sort.key === key
            ? sort.direction === "asc"
              ? "ascending"
              : "descending"
            : "none"
        }
      >
        <button
          onClick={() =>
            setSort({
              key,
              direction:
                sort.key === key && sort.direction === "desc"
                  ? "asc"
                  : key === "name" && sort.key !== key
                  ? "asc"
                  : "desc",
            })
          }
        >
          {label}
          <span className={`le-sort-indicator${sort.key === key ? " active" : ""}`} aria-hidden="true">
            {sort.key === key ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </button>
      </th>
    );
  }
  return (
    <div className="league-evolution">
      <p>{t("intro")}</p>
      <div className="le-switches">
        {[
          ["metric", ["goals", "home", "draw", "promoted", "relegated"], metric, option => {setMetric(option); if (["promoted","relegated"].includes(option)) setMode("actual");}],
          ["mode", ["actual", "expected", "difference"], mode, setMode],
        ].filter(([label]) => !strength || label !== "mode").map(([label, options, value, set]) => (
          <fieldset key={label}>
            <legend>{t(label)}</legend>
            {options.map((option) => (
              <button
                key={option}
                aria-pressed={value === option}
                onClick={() => set(option)}
              >
                {t(option)}
              </button>
            ))}
          </fieldset>
        ))}
      </div>
      <p className="le-note">
        {t(
          strength ? "strengthNote" : metric === "home"
            ? "homeNote"
            : metric === "draw"
            ? "drawNote"
            : "goalsNote"
        )}{" "}
        {!strength && t(
          mode === "actual"
            ? "actualNote"
            : mode === "expected"
            ? "expectedNote"
            : "differenceNote"
        )}
      </p>
      {matrix.collisions.length > 0 && <p role="alert">{t("collisions")}</p>}
      <div className="le-switches">
        <button aria-pressed={showTrend} onClick={()=>setShowTrend(v=>!v)}>{t("trend")}</button>
        <button aria-pressed={showAnalysis} disabled={busy || error} onClick={()=>setShowAnalysis(v=>!v)}>{t("analysis")}</button>
      </div>
      {showTrend && selectedRow && trend && <>
        <LeagueChoice label={t("trendLeague")} value={selectedRow.id} leagues={relevantRows.map(r=>({...r.league,id:r.id,name:r.name}))} onChange={setSelectedLeague} />
        <LeagueEvolutionChart key={`${metric}:${mode}:${selectedRow.id}`} t={t} label={`${selectedRow.name} – ${t(metric)}`}
          format={v=>v==null?'—':new Intl.NumberFormat(i18n?.language||'de',{maximumFractionDigits:2}).format(metric==='draw'?v*100:v)+(metric==='draw'?' %':'')}
          series={trend.summary.series} reference={trend.reference} summary={trend.summary} metric={metric} locale={i18n?.language || "de"}/>
      </>}
      {showAnalysis && analysis && <section aria-label={t("analysis")}>
        <h3>{t("analysis")} · {t(metric)} · {t("all")}</h3><p style={{whiteSpace:'normal'}}>{t("analysisPrepared")}</p>
        <ul>
          <li>{t("long")}: {format(analysis.global.long_term.value)}</li>
          <li>{t("latest")}: {analysis.global.latest?.season || '—'} · {format(analysis.global.latest?.value)}</li>
          <li>{t("minimum")}: {format(analysis.global.minimum)} · {analysis.global.minimum_seasons.join(', ')}</li>
          <li>{t("maximum")}: {format(analysis.global.maximum)} · {analysis.global.maximum_seasons.join(', ')}</li>
        </ul>
        <button onClick={()=>{const blob=new Blob([JSON.stringify(analysis,null,2)],{type:'application/json'});const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=`league-evolution-${metric}.json`;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000)}}>{t("downloadContext")}</button>
        <details><summary>{t("structuredContext")}</summary><pre style={{maxHeight:300,overflow:'auto'}}>{JSON.stringify(analysis,null,2)}</pre></details>
      </section>}
      {busy && <p role="status">{t("loading")}</p>}
      {error && (
        <p role="alert">
          {t("error")}{" "}
          <button onClick={() => setRetry((n) => n + 1)}>{t("retry")}</button>
        </p>
      )}
      {!busy && !records.length && <p>{t("empty")}</p>}
      <p>
        <strong>
          {t("global")}: {cell(all)}
        </strong>{" "}
        <span className="le-note">{t("scope")}</span>
      </p>
      <div
        className="le-scroll"
        tabIndex={0}
        role="region"
        aria-label={t("table")}
      >
        <table>
          <caption>
            {t("table")} · {t(metric)} · {t(strength ? "storedStrength" : mode)}
          </caption>
          <thead>
            <tr>
              {heading("name", t("league"))}
              {heading("long", t("long"))}
              {matrix.seasons.map((s) => heading(s.key, s.label))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row"><LeagueDisplay compact league={row.league}/></th>
                <td>
                  {cell(
                    row.records,
                    null,
                    references[null]
                  )}
                </td>
                {matrix.seasons.map((s) => (
                  <td key={s.key}>
                    {cell(
                      row.records,
                      s.key,
                      references[s.key]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{t("all")}</th>
              <td>{cell(all)}</td>
              {matrix.seasons.map((s) => (
                <td key={s.key}>{cell(all, s.key)}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="le-note">
        {strength ? t("strengthWeighting") : <>{t("missing")} {t("weighting")}</>} {t("comparisonHint")}
      </p>
    </div>
  );
}
